/* ASJ CUSTOM: WebMCP tools (github.com/webmachinelearning/webmcp).
   Exposes callable tools to an AI agent running in the shopper's own browser. Complements Shopify's
   server side UCP/MCP endpoint (/.well-known/ucp): UCP is for remote agents, WebMCP for the agent
   already in the session. Experimental API, so it is feature detected on document.modelContext and
   wrapped in try/catch: without support this file does nothing.
   All tools are READ ONLY except asj_add_to_cart, which only touches the shopper's own cart. Nothing
   here can check out, take payment, read personal data or change store settings. Data comes from
   Shopify's public storefront JSON endpoints. */
(function () {
  'use strict';

  var mc = typeof document !== 'undefined' && document.modelContext;
  if (!mc || typeof mc.registerTool !== 'function') return;

  var ORIGIN = window.location.origin;

  function text(t) { return { content: [{ type: 'text', text: t }] }; }
  function money(c) { return typeof c === 'number' ? '$' + (c / 100).toFixed(2) : ''; }
  function clean(u) { return String(u || '').split('?')[0]; }

  async function getJSON(url) {
    var res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Request failed: ' + res.status);
    return res.json();
  }

  function reg(tool) {
    try { mc.registerTool(tool); } catch (e) { /* never break the page */ }
  }

  function listProducts(items) {
    return items.map(function (p) {
      return '- ' + p.title + (p.price ? ' (' + p.price + ')' : '') + ': ' + ORIGIN + clean(p.url);
    }).join('\n');
  }

  async function suggest(q, limit) {
    var data = await getJSON(ORIGIN + '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=' + (limit || 5));
    return (data.resources && data.resources.results && data.resources.results.products) || [];
  }

  reg({
    name: 'asj_search_products',
    description: 'Search the ASJ Products catalog of fuel additives, greases, oils and lubricants. Returns product names, prices and URLs.',
    inputSchema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search terms, for example "diesel additive" or "NLGI 2 grease"' },
        limit: { type: 'number', description: 'Maximum results, 1 to 10. Default 6.' }
      },
      required: ['query']
    },
    async execute(args) {
      var limit = Math.min(Math.max(parseInt(args && args.limit, 10) || 6, 1), 10);
      try {
        var items = await suggest(args.query, limit);
        if (!items.length) return text('No products matched "' + args.query + '".');
        return text(listProducts(items));
      } catch (e) { return text('Search failed: ' + e.message); }
    }
  });

  reg({
    name: 'asj_get_product',
    description: 'Get full details for one ASJ product: description, price, availability and every size or variant. Omit the handle to describe the product on the current page.',
    inputSchema: {
      type: 'object',
      properties: {
        handle: { type: 'string', description: 'Product handle, the last part of a /products/ URL. Optional on a product page.' }
      }
    },
    async execute(args) {
      var handle = (args && args.handle) || '';
      if (!handle) {
        var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
        if (m) handle = m[1];
      }
      if (!handle) return text('No product handle supplied and the current page is not a product page.');
      try {
        var p = await getJSON(ORIGIN + '/products/' + encodeURIComponent(handle) + '.js');
        var lines = [
          '# ' + p.title,
          'URL: ' + ORIGIN + '/products/' + p.handle,
          'Vendor: ' + (p.vendor || 'n/a'),
          'Available: ' + (p.available ? 'yes' : 'no'),
          '',
          (p.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 900),
          '',
          'Sizes and variants:'
        ];
        (p.variants || []).forEach(function (v) {
          lines.push('- ' + v.title + ': ' + money(v.price) + (v.available ? '' : ' (unavailable)') +
            (v.sku ? ' [SKU ' + v.sku + ']' : '') + ' variantId=' + v.id);
        });
        return text(lines.join('\n'));
      } catch (e) { return text('Could not load product "' + handle + '": ' + e.message); }
    }
  });

  reg({
    name: 'asj_list_categories',
    description: 'List the product categories (collections) available at ASJ Products.',
    inputSchema: { type: 'object', properties: {} },
    async execute() {
      try {
        var data = await getJSON(ORIGIN + '/collections.json?limit=50');
        var cols = (data.collections || []).filter(function (c) { return c.handle !== 'frontpage'; });
        if (!cols.length) return text('No collections returned.');
        return text(cols.map(function (c) {
          return '- ' + c.title + ': ' + ORIGIN + '/collections/' + c.handle;
        }).join('\n'));
      } catch (e) { return text('Could not list categories: ' + e.message); }
    }
  });

  reg({
    name: 'asj_recommend_for_problem',
    description: 'Given an equipment or fuel problem in plain language (for example "clogged fuel filters", "water in gasoline", "bearing wear"), suggest the right ASJ product category and return matching products.',
    inputSchema: {
      type: 'object',
      properties: {
        problem: { type: 'string', description: 'The symptom or problem the equipment is having.' }
      },
      required: ['problem']
    },
    async execute(args) {
      var p = String((args && args.problem) || '').toLowerCase();
      var map = [
        { re: /(water|phase separation|ethanol|gasoline|petrol)/, q: 'gasoline fuel additive', note: 'Gasoline fuel treatment. Phaser 3000 addresses water and phase separation.' },
        { re: /(injector|regen|cetane|filter|sludge|diesel|dpf|idid)/, q: 'diesel fuel additive', note: 'Diesel fuel treatment. Power Klenz ID 5007 targets injector deposits and filter plugging.' },
        { re: /(cold|winter|gel|freeze|cloud point)/, q: 'winter diesel additive', note: 'Cold weather diesel. Winter Klenz 5757 addresses gelling and cold flow.' },
        { re: /(bearing|grease|wear|water washout|high temp|chassis|nlgi)/, q: 'grease moly-d', note: 'Grease with Moly-D. Confirm the NLGI grade the equipment requires.' },
        { re: /(oil pressure|lifter|valve noise|oil consumption|engine wear)/, q: 'oil additive', note: 'Engine oil supplement. 480M Ultragard targets wear and oil pressure.' },
        { re: /(tank|storage|stabiliz|polish|bacteria|algae|microb)/, q: 'fuel tank cleaner stabilizer', note: 'Tank cleaning and fuel stabilization for stored fuel.' },
        { re: /(chain|cable|open gear|sprocket)/, q: 'chain cable open gear lubricant', note: 'Chain, cable and open gear lubricants.' }
      ];
      var hit = null;
      for (var i = 0; i < map.length; i++) { if (map[i].re.test(p)) { hit = map[i]; break; } }
      try {
        var items = await suggest(hit ? hit.q : args.problem, 5);
        var head = hit ? hit.note : 'No direct category match. Showing a keyword search instead.';
        return text(head + '\n\n' + (items.length ? listProducts(items) : 'No products matched.') +
          '\n\nAlways confirm the dose rate and grade on the product page or its technical data sheet before advising a specific treatment.');
      } catch (e) { return text('Lookup failed: ' + e.message); }
    }
  });

  reg({
    name: 'asj_get_policies',
    description: 'Get the ASJ Products shipping and return policy summary, including handling time and the 30 day return window.',
    inputSchema: { type: 'object', properties: {} },
    async execute() {
      return text([
        'Shipping: most in stock orders ship in 1 to 4 business days. Larger freight or special orders may take up to 10 business days. Smaller orders ship UPS, orders over 100 lb may ship by common freight carrier. Cost is carrier calculated at checkout by weight and destination, there is no flat rate.',
        '',
        'Returns: eligible unopened items may be returned within 30 days of receipt, in original packaging with proof of purchase. Start a return by emailing sales@asjproducts.com. Return shipping is paid by the customer unless the item was incorrect, defective or damaged. Hazardous materials, flammable liquids, gases, sale items and gift cards cannot be returned.',
        '',
        'Full policies: ' + ORIGIN + '/policies/shipping-policy and ' + ORIGIN + '/policies/refund-policy'
      ].join('\n'));
    }
  });

  reg({
    name: 'asj_add_to_cart',
    description: 'Add a product variant to the shopper cart on this site. Requires a numeric variantId from asj_get_product. This only changes the cart, it never starts or completes a purchase.',
    inputSchema: {
      type: 'object',
      properties: {
        variantId: { type: 'number', description: 'Numeric variant id from asj_get_product.' },
        quantity: { type: 'number', description: 'How many to add. Default 1.' }
      },
      required: ['variantId']
    },
    async execute(args) {
      var qty = Math.min(Math.max(parseInt(args && args.quantity, 10) || 1, 1), 99);
      try {
        var res = await fetch(ORIGIN + '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: args.variantId, quantity: qty }] })
        });
        if (!res.ok) {
          var err = await res.json().catch(function () { return {}; });
          return text('Could not add to cart: ' + (err.description || err.message || res.status));
        }
        var data = await res.json();
        var item = (data.items && data.items[0]) || {};
        return text('Added ' + qty + ' x ' + (item.title || 'item') + ' to the cart. The shopper still needs to review the cart and approve checkout at ' + ORIGIN + '/cart');
      } catch (e) { return text('Could not add to cart: ' + e.message); }
    }
  });
})();
